import { User } from 'lucide-react';
import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useEmail } from '@/components/providers/EmailProvider/EmailProvider';
import { Field, FieldLabel } from '@/components/shadcn/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select';
import { getPolicyRef } from '@/features/crm/utils/anva/utils';
import type { Party } from '@/features/party/types';
import usePolicies from '@/features/policies/queries/hooks/usePolicies';
import type { Policy } from '@/features/policies/types';
import { getPartyName } from '@/lib/party';

import PolicySelectItem from '../PolicySelectItem';

type Props<T extends FieldValues> = {
  control: Control<T>;
  name?: Path<T>;
  party: Maybe<Party>;
};

export default function AnvaTargetField<T extends FieldValues>({
  control,
  name,
  party,
}: Props<T>) {
  const { t } = useTranslation();

  const {
    analysis: {
      relevant: { policyIds },
    },
  } = useEmail();

  const { data: policies } = usePolicies(
    party?.id ?? '',
    { activeOnly: true },
    { enabled: Boolean(party?.id) },
  );

  const isRelevant = (policy: Policy) =>
    Boolean(policyIds?.includes(policy.id));

  const anvaPolicies = policies
    ?.filter((p) => p.external_refs?.find((r) => r.company === 'ANVA'))
    .sort((a, b) => Number(isRelevant(b)) - Number(isRelevant(a)));

  return (
    <Controller
      control={control}
      name={name ?? ('target' as Path<T>)}
      render={({ field }) => (
        <Field>
          <FieldLabel>{t('TASK_FIELD_TARGET')}</FieldLabel>
          <Select
            value={`${field.value.type}:${field.value.id}`}
            onValueChange={(value) =>
              field.onChange({
                type: value.split(':')[0],
                id: value.split(':')[1],
              })
            }
          >
            <SelectTrigger
              className="w-full *:data-[slot=select-value]:flex-1"
              data-testid="task-priority-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={`party:${party?.id}`}>
                <User />
                {getPartyName(party)}
              </SelectItem>

              {anvaPolicies?.map((policy) => (
                <PolicySelectItem
                  key={policy.id}
                  value={`policy:${getPolicyRef(policy)}`}
                  policy={policy}
                  isRelevant={isRelevant(policy)}
                />
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}
    />
  );
}
